import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { API_URL } from "../config";

const AUTOPLAY_MS = 7000;

function track(path) {
  // Счётчики показов и кликов; сбой не должен влиять на страницу
  try { fetch(`${API_URL}${path}`, { method: "POST", keepalive: true }).catch(() => {}); } catch (e) { /* noop */ }
}

function BannerSlide({ b, onClick }) {
  const isExternal = /^https?:\/\//i.test(b.link_url || "");
  const label = b.button_label || "Узнать больше";
  const cta = b.link_url && (
    isExternal
      ? <a href={b.link_url} target="_blank" rel="noopener noreferrer" className="slide-btn" onClick={onClick}>{label} <ArrowRight size={15} strokeWidth={2.2} /></a>
      : <Link to={b.link_url} className="slide-btn" onClick={onClick}>{label} <ArrowRight size={15} strokeWidth={2.2} /></Link>
  );
  return (
    <div className={`slide slide-${b.theme || "teal"}`}>
      <div className="slide-body">
        <div className="slide-title">{b.title}</div>
        {b.text && <div className="slide-text">{b.text}</div>}
        {cta}
      </div>
      {b.image_url && <img src={b.image_url} alt="" className="slide-image" loading="lazy" />}
    </div>
  );
}

/** Слайдер главной: первым слайдом идёт приветствие (hero), дальше — анонсы и новости из админки.
 *  Без активных баннеров показывает только hero, без элементов управления. */
export default function BannerSlider({ specialtyId, hero }) {
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const seen = useRef(new Set());

  useEffect(() => {
    const qs = specialtyId ? `?specialty=${encodeURIComponent(specialtyId)}` : "";
    fetch(`${API_URL}/banners${qs}`)
      .then(r => (r.ok ? r.json() : []))
      .then(list => { setBanners(Array.isArray(list) ? list : []); setIndex(0); })
      .catch(() => setBanners([]));
  }, [specialtyId]);

  const count = banners.length + 1;
  const go = useCallback(i => setIndex(((i % count) + count) % count), [count]);

  // автопрокрутка (отключается при наведении/фокусе и при prefers-reduced-motion)
  useEffect(() => {
    if (count < 2 || paused) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setTimeout(() => setIndex(i => (i + 1) % count), AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [index, count, paused]);

  // показ засчитывается один раз за загрузку страницы, когда слайд стал активным
  useEffect(() => {
    const b = banners[index - 1];
    if (b && !seen.current.has(b.id)) {
      seen.current.add(b.id);
      track(`/banners/${b.id}/view`);
    }
  }, [index, banners]);

  if (!banners.length) return <div style={{ marginBottom: 24 }}>{hero}</div>;

  return (
    <div
      className="slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      role="region"
      aria-roledescription="карусель"
      aria-label="Анонсы и новости портала"
    >
      <div className="slider-stage">
        <div className={`slider-item${index === 0 ? " active" : ""}`} aria-hidden={index !== 0}>{hero}</div>
        {banners.map((b, i) => (
          <div key={b.id} className={`slider-item${index === i + 1 ? " active" : ""}`} aria-hidden={index !== i + 1}>
            <BannerSlide b={b} onClick={() => track(`/banners/${b.id}/click`)} />
          </div>
        ))}
      </div>
      <button type="button" className="slider-arrow prev" onClick={() => go(index - 1)} aria-label="Предыдущий слайд"><ChevronLeft size={20} /></button>
      <button type="button" className="slider-arrow next" onClick={() => go(index + 1)} aria-label="Следующий слайд"><ChevronRight size={20} /></button>
      <div className="slider-dots">
        {Array.from({ length: count }).map((_, i) => (
          <button key={i} type="button" className={`slider-dot${i === index ? " active" : ""}`} onClick={() => go(i)} aria-label={`Слайд ${i + 1}`} aria-current={i === index} />
        ))}
      </div>
    </div>
  );
}
